import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Phone, ArrowRight, CheckCircle, AlertTriangle, Lightbulb, ThermometerSun, Building2, Zap, Sun } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';

const CaseStudyExeterOfficeWorkshop = () => {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Commercial EPC Case Study: Office and Workshop Premises Near Exeter",
    "author": { "@type": "Organization", "name": "Energy Performance Direct" },
    "publisher": { "@type": "Organization", "name": "Energy Performance Direct", "url": "https://energyperformancedirect.co.uk" },
    "description": "Real case study of a commercial EPC assessment for a 793 sq m office and workshop building near Exeter, including SBEM modelling, energy advice and improvement recommendations."
  };

  return (
    <>
      <Helmet>
        <title>Commercial EPC Case Study | Office & Workshop Near Exeter | Energy Performance Direct</title>
        <meta name="description" content="Real case study: Commercial EPC assessment for a 793 sq m office and workshop building near Exeter. Oil boiler replacement, LED lighting upgrade and MEES compliance recommendations." />
        <meta name="keywords" content="commercial EPC case study, office EPC Exeter, workshop EPC Devon, SBEM assessment case study, MEES compliance Exeter, commercial energy assessment, non-domestic EPC case study" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/case-study-exeter-office-workshop" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <Navbar />
      <Breadcrumbs items={[
        { label: 'Industrial EPC', path: '/industrial-epc' },
        { label: 'Case Study: Office & Workshop', path: '/case-study-exeter-office-workshop' }
      ]} />

      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-16" data-testid="case-study-hero">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-green-500/20 text-green-300 px-4 py-2 rounded-full text-sm mb-6">
              <Building2 className="w-4 h-4" />
              Real Case Study
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Office and Workshop EPC Assessment Near Exeter
            </h1>
            <p className="text-lg text-gray-300 mb-4">
              How we assessed a mixed-use commercial building and identified a clear pathway from a G rating to Band A through staged improvements.
            </p>
            <div className="flex flex-wrap gap-4 text-sm text-gray-400 mt-6">
              <span className="bg-gray-700 px-3 py-1 rounded-full">793.5 sq m</span>
              <span className="bg-gray-700 px-3 py-1 rounded-full">Near Exeter, Devon</span>
              <span className="bg-gray-700 px-3 py-1 rounded-full">Offices & Workshop</span>
              <span className="bg-gray-700 px-3 py-1 rounded-full">MEES Compliance</span>
            </div>
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Project Overview</h2>
            <div className="prose prose-lg max-w-none text-gray-700">
              <p>
                Energy Performance Direct was commissioned to carry out a full non-domestic energy assessment of a commercial building located near Exeter in Devon. The property, totalling 793.5 square metres of useful floor area, comprises office accommodation and workshop space. The building is heated by an oil-fired boiler system with air conditioning serving the office areas.
              </p>
              <p>
                The client required a commercial Energy Performance Certificate for regulatory compliance, along with a detailed Energy Advice Report setting out a practical improvement roadmap. With the building classified at NOS3 complexity level, our Quidos-accredited assessor conducted a thorough <Link to="/sbem-explained" className="text-green-600 hover:underline">SBEM assessment</Link> covering the full building envelope, heating and cooling systems, lighting installations and hot water provision.
              </p>
            </div>

            {/* Key Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
              <Card className="border-none bg-red-50">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-red-600">G</p>
                  <p className="text-sm text-gray-600 mt-1">Baseline Rating</p>
                </CardContent>
              </Card>
              <Card className="border-none bg-gray-50">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-gray-900">793.5</p>
                  <p className="text-sm text-gray-600 mt-1">Floor Area (sq m)</p>
                </CardContent>
              </Card>
              <Card className="border-none bg-blue-50">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-blue-600">70.84</p>
                  <p className="text-sm text-gray-600 mt-1">kWh/m2/yr</p>
                </CardContent>
              </Card>
              <Card className="border-none bg-green-50">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-green-600">19.4</p>
                  <p className="text-sm text-gray-600 mt-1">kgCO2/m2/yr BER</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Energy Breakdown */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Energy and Carbon Breakdown</h2>
            <p className="text-gray-700 mb-8">
              Our SBEM assessment identified the proportional contribution of each building system to the overall carbon emissions. This breakdown is essential for targeting improvements where they will have the greatest impact.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Lightbulb className="w-6 h-6 text-amber-500" />
                      <h3 className="font-semibold text-lg">Lighting</h3>
                    </div>
                    <span className="text-2xl font-bold text-amber-600">48.5%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
                    <div className="bg-amber-500 h-3 rounded-full" style={{width: '48.5%'}}></div>
                  </div>
                  <p className="text-sm text-gray-600">The dominant source of carbon emissions. The building uses older fluorescent lighting (T12 and T8 tubes) throughout both the office and workshop areas. Upgrading to LED would deliver the single largest improvement.</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Zap className="w-6 h-6 text-blue-500" />
                      <h3 className="font-semibold text-lg">Hot Water</h3>
                    </div>
                    <span className="text-2xl font-bold text-blue-600">29.7%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
                    <div className="bg-blue-500 h-3 rounded-full" style={{width: '29.7%'}}></div>
                  </div>
                  <p className="text-sm text-gray-600">The oil-fired system serving hot water contributes nearly a third of emissions. Replacing with electric point-of-use heaters eliminates the oil dependency and reduces both emissions and running costs.</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <ThermometerSun className="w-6 h-6 text-red-500" />
                      <h3 className="font-semibold text-lg">Heating</h3>
                    </div>
                    <span className="text-2xl font-bold text-red-600">19.8%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
                    <div className="bg-red-500 h-3 rounded-full" style={{width: '19.8%'}}></div>
                  </div>
                  <p className="text-sm text-gray-600">The oil boiler heating the offices and workshop areas accounts for a fifth of carbon output. A split or multi-split air conditioning system with COP 3.5 or better would dramatically reduce this figure.</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Building2 className="w-6 h-6 text-teal-500" />
                      <h3 className="font-semibold text-lg">Cooling</h3>
                    </div>
                    <span className="text-2xl font-bold text-teal-600">2.1%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
                    <div className="bg-teal-500 h-3 rounded-full" style={{width: '2.1%'}}></div>
                  </div>
                  <p className="text-sm text-gray-600">Cooling accounts for a small proportion of total emissions. Some spaces exceeded the solar gain limit defined in the National Calculation Methodology, suggesting consideration of solar shading measures.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Key Challenges */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Key Challenges Identified</h2>
            <div className="prose prose-lg max-w-none text-gray-700">
              <p>
                The SBEM assessment and subsequent Energy Advice Report identified several factors contributing to the building's poor baseline energy performance:
              </p>
            </div>
            <div className="space-y-4 mt-6">
              {[
                { icon: AlertTriangle, title: 'Oil-Fired Heating', desc: 'The building relied on an oil boiler for space heating and hot water. Oil is one of the highest-carbon fuels for commercial heating, and its use significantly depressed the EPC rating. Removing the oil boiler was identified as the single most impactful improvement.' },
                { icon: Lightbulb, title: 'Outdated Fluorescent Lighting', desc: 'The building used 38mm (T12) and 26mm (T8) fluorescent tubes throughout. With lighting responsible for 48.5% of carbon emissions, this represented a major opportunity for improvement through LED replacement.' },
                { icon: Building2, title: 'Mixed Glazing and Insulation', desc: 'The building featured a combination of single-glazed and double-glazed windows. Solid walls lacked adequate insulation, cavity walls had uninsulated cavities, and some loft spaces were poorly insulated, contributing to significant heat loss.' },
                { icon: ThermometerSun, title: 'Solar Overheating Risk', desc: 'The assessment identified spaces where solar gain exceeded the limits defined in the National Calculation Methodology, highlighting overheating risk without adequate solar control measures.' }
              ].map((item, i) => (
                <div key={i} className="flex gap-4 p-4 bg-gray-50 rounded-lg">
                  <item.icon className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                    <p className="text-gray-700 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Staged Improvement Roadmap */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8">Staged Improvement Roadmap</h2>
            <p className="text-gray-300 mb-8">
              Our Energy Advice Report provided a phased improvement strategy, allowing the client to prioritise investment and achieve progressively better ratings. Each stage builds on the previous one, offering a practical pathway from the baseline G rating towards Band A.
            </p>
            <div className="space-y-8">
              {/* Stage 1 */}
              <div className="border-l-4 border-green-500 pl-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="bg-green-600 text-white text-sm font-bold px-3 py-1 rounded-full">Stage 1</span>
                  <span className="text-green-400 font-semibold">Target: Band C - MEES Compliance</span>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300"><strong className="text-white">Replace the oil boiler</strong> with a split or multi-split air conditioning system for the office areas, achieving a Coefficient of Performance (COP) of 3.5 and Energy Efficiency Ratio (EER) of 3.5 or better</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300"><strong className="text-white">Replace the hot water system</strong> with electric point-of-use water heaters, eliminating oil dependency for domestic hot water</span>
                  </li>
                </ul>
                <p className="text-sm text-gray-400 mt-3">Removing the oil boiler is identified as the single biggest improvement. This alone achieves <Link to="/mees-commercial-buildings" className="text-green-400 hover:underline">MEES compliance</Link> by bringing the rating to Band C.</p>
              </div>

              {/* Stage 2 */}
              <div className="border-l-4 border-blue-500 pl-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="bg-blue-600 text-white text-sm font-bold px-3 py-1 rounded-full">Stage 2</span>
                  <span className="text-blue-400 font-semibold">Target: Band B</span>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300"><strong className="text-white">Replace all fixed lighting with LEDs</strong> achieving efficiencies of 100 lumens per circuit watt with a light output ratio (LOR) of 100% or better</span>
                  </li>
                </ul>
                <p className="text-sm text-gray-400 mt-3">With lighting accounting for 48.5% of carbon emissions, this single measure delivers a substantial rating improvement and ongoing energy cost reduction.</p>
              </div>

              {/* Stage 3 */}
              <div className="border-l-4 border-amber-500 pl-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="bg-amber-600 text-white text-sm font-bold px-3 py-1 rounded-full">Stage 3</span>
                  <span className="text-amber-400 font-semibold">Target: Band A - Comprehensive Upgrade</span>
                </div>
                <ul className="space-y-3">
                  {[
                    'Replace all single-glazed and double-glazed windows with new units achieving U-values of 1.4 W/m2K or better',
                    'Insulate external walls to achieve a U-value of 0.27 W/m2K or better',
                    'Insulate flat and pitched roofs to a U-value of 0.25 W/m2K or better',
                    'Install 5 kWp or more of solar PV on the south-west facing roof'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-gray-400 mt-3">This comprehensive fabric-first approach combined with renewable generation targets the highest achievable rating. The advice report also recommends consideration of Phase 3 electrical upgrades for future-proofing, including provisions for heat pumps, EV charging and battery storage.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Recommendations */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Additional EPC Recommendations</h2>
            <p className="text-gray-700 mb-6">
              Beyond the staged improvement roadmap, the EPC recommendations report identified further measures categorised by payback period:
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-3 text-green-700">Short Payback (&lt;3 years)</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Replace T12 fluorescent tubes with T8 tubes on failure</span></li>
                    <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Apply solar control measures to windows exceeding NCM solar gain limits</span></li>
                  </ul>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-3 text-amber-700">Medium Payback (3-7 years)</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" /><span>Replace T8 lamps with retrofit T5 conversion kits</span></li>
                  </ul>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-3 text-blue-700">Long Payback (&gt;7 years)</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" /><span>High frequency ballasts for fluorescent tubes</span></li>
                    <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" /><span>Building-mounted wind turbines</span></li>
                    <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" /><span>Solar water heating system</span></li>
                    <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" /><span>Solar PV installation</span></li>
                    <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" /><span>Optimum start/stop heating controls</span></li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Key Takeaways */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Key Takeaways</h2>
            <div className="space-y-4">
              {[
                'Oil-fired heating is one of the biggest factors holding back commercial EPC ratings. Switching to electric heat pump or split AC systems can deliver a step-change improvement.',
                'Lighting often accounts for the largest share of carbon emissions in commercial and industrial buildings. LED upgrades offer rapid payback and significant rating improvements.',
                'A staged approach to improvement allows building owners to spread investment while achieving MEES compliance at Stage 1, before pursuing higher ratings.',
                'Comprehensive fabric upgrades (windows, walls, roof) combined with renewable energy generation can bring even a G-rated building up to Band A over time.',
                'Professional SBEM modelling allows each improvement to be quantified before committing expenditure, reducing risk and optimising investment decisions.'
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-white rounded-lg shadow-sm">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Services and Guides</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link to="/industrial-epc" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold mb-1">Industrial EPCs</h3>
                <p className="text-sm text-gray-600">Warehouse, factory and workshop assessments</p>
              </Link>
              <Link to="/sbem-explained" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold mb-1">SBEM Explained</h3>
                <p className="text-sm text-gray-600">How commercial EPC ratings are calculated</p>
              </Link>
              <Link to="/mees-commercial-buildings" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold mb-1">MEES Regulations</h3>
                <p className="text-sm text-gray-600">Minimum energy efficiency standards guide</p>
              </Link>
              <Link to="/workshop-epc" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold mb-1">Workshop EPCs</h3>
                <p className="text-sm text-gray-600">Light industrial and trade premises</p>
              </Link>
              <Link to="/industrial-epc-devon" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold mb-1">Industrial EPCs Devon</h3>
                <p className="text-sm text-gray-600">Devon industrial EPC assessments</p>
              </Link>
              <Link to="/commercial-epc-devon" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold mb-1">Commercial EPCs Devon</h3>
                <p className="text-sm text-gray-600">All commercial buildings in Devon</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-green-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Need a Commercial or Industrial EPC?</h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Our accredited assessors provide detailed EPC assessments and Energy Advice Reports for commercial and industrial buildings across Devon, Cornwall, Somerset and Dorset.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-green-600 hover:bg-gray-100">
              <Link to="/booking?service=commercial_warehouse">
                Request Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
              <a href="tel:01392980077">
                <Phone className="mr-2 w-5 h-5" />
                Call 01392 980077
              </a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default CaseStudyExeterOfficeWorkshop;
